// Leads board — all filtering, data reads and inline actions happen here via fetch(), so the page
// never does a full reload (parity with the legacy ShowLeadTable AJAX grid). The grid body is
// fetched from /Leads/Grid and swapped into #leadGrid; inline actions POST to the existing endpoints
// (which return a {ok,msg,err} JSON envelope for XHR callers) and then re-fetch the grid.

interface Envelope { ok: boolean; msg?: string | null; err?: string | null; waUrl?: string | null; offerUrl?: string | null; }

const esc = (s: unknown): string =>
  String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const FILTER_KEYS = ["status", "text", "agent", "color", "archived"] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

export function initLeadsBoard(): void {
  const boardMaybe = document.getElementById("leadsBoard");
  if (!boardMaybe) return;
  const board: HTMLElement = boardMaybe;

  const grid = document.getElementById("leadGrid")!;
  const tokenEl = board.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]');
  const token = () => tokenEl?.value ?? "";

  const state: Record<FilterKey, string> = { status: "all", text: "", agent: "", color: "", archived: "false" };
  // Seed from the current URL so a refresh/bookmark restores the view.
  const seed = new URLSearchParams(location.search);
  for (const k of FILTER_KEYS) if (seed.has(k)) state[k] = seed.get(k) || "";
  if (!state.status) state.status = "all";

  const params = () => {
    const p = new URLSearchParams();
    for (const k of FILTER_KEYS) if (state[k] && state[k] !== "false") p.set(k, state[k]);
    return p;
  };

  // --- toast (TempData-style flash) ---
  let toastT = 0;
  function toast(msg: string, isErr = false) {
    let t = document.getElementById("leadToast");
    if (!t) {
      t = document.createElement("div");
      t.id = "leadToast";
      t.className = "fixed bottom-4 start-1/2 z-[60] -translate-x-1/2 rounded-lg px-4 py-2 text-sm shadow-lg";
      document.body.appendChild(t);
    }
    t.className = `fixed bottom-4 start-1/2 z-[60] -translate-x-1/2 rounded-lg px-4 py-2 text-sm shadow-lg ${isErr ? "bg-error-600 text-white" : "bg-gray-900 text-white"}`;
    t.textContent = msg;
    t.style.opacity = "1";
    window.clearTimeout(toastT);
    toastT = window.setTimeout(() => { t!.style.opacity = "0"; }, 2600);
  }

  // --- grid fetch + swap ---
  let busy = false;
  async function fetchGrid(push = true) {
    if (busy) return;
    busy = true;
    grid.style.opacity = "0.5";
    try {
      const res = await fetch(`/Leads/Grid?${params().toString()}`, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      grid.innerHTML = await res.text();
      syncChrome();
      if (push) history.pushState(state, "", `/Leads?${params().toString()}`);
    } catch {
      toast("שגיאה בטעינת הרשימה", true);
    } finally {
      grid.style.opacity = "1";
      busy = false;
    }
  }

  // Reflect state onto the tabs / colour chips / count.
  function syncChrome() {
    board.querySelectorAll<HTMLElement>("[data-tab]").forEach((el) =>
      el.classList.toggle("active", (el.dataset.tab || "") === (state.status || "all")));
    board.querySelectorAll<HTMLElement>("[data-colorfilter]").forEach((el) => {
      const v = el.dataset.colorfilter || "";
      const on = v === state.color || (v === "" && !state.color);
      el.classList.toggle("active", on && !el.classList.contains("color-chip"));
      el.classList.toggle("ring-2", on && el.classList.contains("color-chip"));
      el.classList.toggle("ring-brand-300", on && el.classList.contains("color-chip"));
    });
    const meta = grid.querySelector<HTMLElement>("[data-count]");
    const hdr = document.getElementById("leadHeadCount");
    if (meta && hdr) hdr.textContent = meta.dataset.count || "0";
  }

  // --- POST an action, then refresh the grid ---
  async function post(url: string, data: Record<string, string | string[]>): Promise<Envelope | null> {
    const body = new URLSearchParams();
    body.set("__RequestVerificationToken", token());
    for (const [k, v] of Object.entries(data)) {
      if (Array.isArray(v)) v.forEach((x) => body.append(k, x));
      else body.set(k, v);
    }
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "X-Requested-With": "XMLHttpRequest", "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (!res.ok) { toast("שגיאה", true); return null; }
      const env = (await res.json()) as Envelope;
      if (env.err) toast(env.err, true);
      else if (env.msg) toast(env.msg);
      return env;
    } catch {
      toast("שגיאת רשת", true);
      return null;
    }
  }
  async function act(url: string, data: Record<string, string | string[]>) {
    const env = await post(url, data);
    if (env?.ok) await fetchGrid(false);
  }

  // --- modals (popup iframe / call / offer) ---
  // Toggle inline display (not the `hidden` class) so a modal can show as flex reliably.
  const show = (id: string) => { const e = document.getElementById(id); if (e) e.style.display = e.dataset.display || "flex"; };
  const hide = (id: string) => { const e = document.getElementById(id); if (e) e.style.display = "none"; };
  const isOpen = (id: string) => { const e = document.getElementById(id); return !!e && e.style.display !== "none"; };

  function openPopup(url: string, title: string) {
    const f = document.getElementById("leadPopupFrame") as HTMLIFrameElement | null;
    const t = document.getElementById("leadPopupTitle");
    const openTab = document.getElementById("leadPopupTab") as HTMLAnchorElement | null;
    if (f) f.src = url;
    if (t) t.textContent = title;
    if (openTab) openTab.href = url;
    show("leadPopup");
  }
  function closePopup() {
    const f = document.getElementById("leadPopupFrame") as HTMLIFrameElement | null;
    if (f) f.src = "about:blank";
    hide("leadPopup");
    fetchGrid(false); // the lead may have changed inside the popup
  }

  function openModal(id: string, leadId: string, name: string) {
    const m = document.getElementById(id);
    if (!m) return;
    m.querySelector<HTMLInputElement>("[data-lead-id]")!.value = leadId;
    const nm = m.querySelector<HTMLElement>("[data-lead-name]");
    if (nm) nm.textContent = name;
    m.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("input:not([type=hidden]), textarea, select")
      .forEach((el) => {
        // A select has no "" option once it is a plain value list, and value="" would leave the box
        // blank - reset it to its first option instead.
        if (el instanceof HTMLSelectElement) el.selectedIndex = 0;
        else if (el.type !== "hidden") el.value = "";
      });
    show(id);
  }

  // --- selection / bulk bar ---
  function checks() { return Array.from(grid.querySelectorAll<HTMLInputElement>(".lead-check")); }
  function selectedIds() { return checks().filter((c) => c.checked).map((c) => c.dataset.id!); }
  function refreshBulkBar() {
    const n = selectedIds().length;
    const bar = document.getElementById("bulkBar");
    const cnt = document.getElementById("bulkCount");
    if (bar) bar.style.display = n === 0 ? "none" : "flex";
    if (cnt) cnt.textContent = String(n);
  }

  // ================= event wiring (delegated; survives grid swaps) =================
  board.addEventListener("click", (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>("[data-tab],[data-colorfilter],[data-action],[data-archive-toggle]");
    if (!t) return;

    if (t.dataset.tab !== undefined) { e.preventDefault(); state.status = t.dataset.tab || "all"; fetchGrid(); return; }
    if (t.dataset.colorfilter !== undefined) { e.preventDefault(); state.color = t.dataset.colorfilter || ""; fetchGrid(); return; }
    if (t.dataset.archiveToggle !== undefined) {
      e.preventDefault();
      state.archived = state.archived === "true" ? "false" : "true";
      t.textContent = state.archived === "true" ? "פעילים" : "ארכיון";
      fetchGrid();
      return;
    }

    const a = t.dataset.action;
    if (!a) return;
    const id = t.dataset.id || "";
    if (a === "status") { closeDetails(t); act("/Leads/ChangeStatus", { id, status: t.dataset.status || "" }); }
    else if (a === "color") { closeDetails(t); act("/Leads/SetColor", { id, value: t.dataset.value || "" }); }
    else if (a === "open") openPopup(t.dataset.url || "", t.dataset.title || "");
    else if (a === "call") {
      openModal("callModal", id, t.dataset.title || "");
      // Flight date is read-only context in the popup, so it is pushed in rather than posted back.
      const fd = document.querySelector<HTMLElement>("#callModal [data-lead-fltdate]");
      if (fd) fd.textContent = t.dataset.fltdate || "—";
    }
    else if (a === "offer") openOffer(id, t.dataset.title || "");
  });

  function closeDetails(el: HTMLElement) { el.closest("details")?.removeAttribute("open"); }

  board.addEventListener("change", (e) => {
    const el = e.target as HTMLElement;
    if (el.classList.contains("lead-assign")) {
      const sel = el as HTMLSelectElement;
      act("/Leads/Assign", { id: sel.dataset.id || "", agent: sel.value });
    } else if (el.classList.contains("lead-check-all")) {
      const on = (el as HTMLInputElement).checked;
      checks().forEach((c) => { c.checked = on; });
      refreshBulkBar();
    } else if (el.classList.contains("lead-check")) {
      refreshBulkBar();
    }
  });

  // Toolbar search / agent filter (a <form data-filter-form>)
  const filterForm = document.querySelector<HTMLFormElement>("[data-filter-form]");
  filterForm?.addEventListener("submit", (e) => {
    e.preventDefault();
    state.text = (filterForm.querySelector('[name="text"]') as HTMLInputElement)?.value || "";
    const ag = filterForm.querySelector('[name="agent"]') as HTMLSelectElement | null;
    if (ag) state.agent = ag.value;
    fetchGrid();
  });

  // Bulk bar buttons
  document.getElementById("bulkStatusBtn")?.addEventListener("click", async () => {
    const st = (document.getElementById("bulkStatusSel") as HTMLSelectElement)?.value;
    if (!st) return;
    await act("/Leads/BulkStatus", { status: st, ids: selectedIds() });
  });
  document.getElementById("bulkAssignBtn")?.addEventListener("click", async () => {
    const ag = (document.getElementById("bulkAssignSel") as HTMLSelectElement)?.value ?? "";
    await act("/Leads/BulkAssign", { agent: ag, ids: selectedIds() });
  });
  document.getElementById("bulkClear")?.addEventListener("click", () => {
    checks().forEach((c) => { c.checked = false; });
    const all = grid.querySelector<HTMLInputElement>(".lead-check-all");
    if (all) all.checked = false;
    refreshBulkBar();
  });

  // Modal wiring (close buttons + submit)
  document.querySelectorAll<HTMLElement>("[data-close-modal]").forEach((btn) =>
    btn.addEventListener("click", () => {
      const m = btn.dataset.closeModal!;
      if (m === "leadPopup") closePopup(); else hide(m);
    }));
  document.getElementById("leadPopup")?.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).id === "leadPopup") closePopup();
  });
  ["callModal", "offerModal"].forEach((id) =>
    document.getElementById(id)?.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).id === id) hide(id); // click on the overlay itself
    }));
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      ["callModal", "offerModal"].forEach((id) => hide(id));
      if (isOpen("leadPopup")) closePopup();
    }
  });

  function wireModalForm(id: string, url: string) {
    const form = document.getElementById(id) as HTMLFormElement | null;
    form?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const data: Record<string, string> = {};
      new FormData(form).forEach((v, k) => { if (k !== "__RequestVerificationToken") data[k] = String(v); });
      const env = await post(url, data);
      if (env?.ok) { hide(id); await fetchGrid(false); }
    });
  }
  wireModalForm("callModal", "/Leads/LogCall");

  // ---- Offer modal (package builder → link to /Offer/{code}) ----
  const offerModalEl = document.getElementById("offerModal");
  const roomsBox = () => document.getElementById("offerRooms")!;
  const roomTpl = document.getElementById("offerRoomTpl") as HTMLTemplateElement | null;
  const offerVal = (name: string) =>
    (offerModalEl?.querySelector(`[data-offer="${name}"]`) as HTMLInputElement | HTMLTextAreaElement | null)?.value.trim() || "";

  function addRoomRow() { if (roomTpl) roomsBox().appendChild(roomTpl.content.cloneNode(true)); }
  function gatherRooms() {
    return Array.from(roomsBox().querySelectorAll<HTMLElement>(".offer-room"))
      .map((r) => ({
        hotel: (r.querySelector(".room-hotel") as HTMLInputElement).value.trim(),
        roomType: (r.querySelector(".room-type") as HTMLInputElement).value.trim(),
        beds: Number((r.querySelector(".room-beds") as HTMLInputElement).value) || 0,
        pricePerPerson: Number((r.querySelector(".room-price") as HTMLInputElement).value) || 0,
      }))
      .filter((r) => r.hotel || r.pricePerPerson);
  }
  function switchOfferTab(which: string) {
    offerModalEl?.querySelectorAll<HTMLElement>("[data-offer-pane]").forEach((p) => { p.style.display = p.dataset.offerPane === which ? "" : "none"; });
    offerModalEl?.querySelectorAll<HTMLElement>("[data-offer-tab]").forEach((b) => {
      const on = b.dataset.offerTab === which;
      b.classList.toggle("text-brand-700", on);
      b.classList.toggle("font-medium", on);
      b.classList.toggle("text-gray-500", !on);
    });
  }
  async function loadOfferHistory(id: string) {
    const box = document.getElementById("offerHistory");
    if (box) box.textContent = "טוען…";
    try {
      const res = await fetch(`/Leads/OfferHistory?id=${encodeURIComponent(id)}`, { headers: { "X-Requested-With": "XMLHttpRequest" } });
      const data = await res.json();
      const rows: Array<Record<string, unknown>> = data.offers || [];
      if (!box) return;
      box.innerHTML = rows.length === 0
        ? '<p class="text-gray-400">אין הצעות עדיין</p>'
        : `<div class="table-wrap"><table class="table table-dense"><thead><tr><th>תאריך</th><th>יעד</th><th>מחיר</th><th>ערוץ</th><th>צפיות</th><th></th></tr></thead><tbody>${rows
            .map((o) => `<tr><td>${esc(o.utc)}</td><td>${esc(o.destination)}</td><td dir="ltr">₪${Number(o.amount || 0).toLocaleString()}</td><td>${esc(o.channel)}</td><td>${esc(o.views)}</td><td>${o.url ? `<a href="${esc(o.url)}" target="_blank" class="text-brand-600 hover:underline">פתח</a>` : ""}</td></tr>`)
            .join("")}</tbody></table></div>`;
    } catch { if (box) box.textContent = "שגיאה בטעינה"; }
  }
  function openOffer(id: string, name: string) {
    if (!offerModalEl) return;
    (offerModalEl.querySelector("[data-lead-id]") as HTMLInputElement).value = id;
    const nm = offerModalEl.querySelector("[data-lead-name]"); if (nm) nm.textContent = name;
    offerModalEl.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("[data-offer]").forEach((el) => { el.value = ""; });
    roomsBox().innerHTML = "";
    addRoomRow();
    switchOfferTab("compose");
    show("offerModal");
    loadOfferHistory(id);
  }
  async function sendOffer(channel: string) {
    const id = (offerModalEl?.querySelector("[data-lead-id]") as HTMLInputElement)?.value;
    if (!id) return;
    const env = await post("/Leads/SendOffer", {
      id, channel,
      amount: offerVal("amount") || "0",
      destination: offerVal("destination"),
      nights: offerVal("nights") || "0",
      passengers: offerVal("passengers") || "0",
      personalMessage: offerVal("personalMessage"),
      body: offerVal("body"),
      roomsJson: JSON.stringify(gatherRooms()),
      validHours: offerVal("validHours"),
      checkedAt: offerVal("checkedAt"),
    });
    if (env?.ok) {
      if (env.waUrl) window.open(env.waUrl, "_blank");
      hide("offerModal");
      await fetchGrid(false);
    }
  }

  document.getElementById("offerAddRoom")?.addEventListener("click", addRoomRow);
  offerModalEl?.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    if (t.closest(".offer-room-del")) { t.closest(".offer-room")?.remove(); return; }
    const tab = t.closest<HTMLElement>("[data-offer-tab]"); if (tab) { switchOfferTab(tab.dataset.offerTab!); return; }
    const send = t.closest<HTMLElement>("[data-offer-send]"); if (send) { sendOffer(send.dataset.offerSend!); return; }
  });

  // Back/forward navigation → re-read filters, no push.
  window.addEventListener("popstate", () => {
    const q = new URLSearchParams(location.search);
    for (const k of FILTER_KEYS) state[k] = q.get(k) || (k === "status" ? "all" : k === "archived" ? "false" : "");
    fetchGrid(false);
  });

  // Grid is already server-rendered on first load; just wire the initial chrome/selection.
  syncChrome();
  refreshBulkBar();
}
